import { useCallback, useEffect, useState } from "react";
import { useClientId } from "@/shared/hooks/useClientId";
import { Button } from "@/shared/ui/button";
import { Skeleton } from "@/shared/ui/skeleton";
import { toast } from "sonner";
import { Copy, KeyRound, RefreshCw } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import {
  getFirebaseIdToken,
  getFirebaseIdTokenWithRefresh,
} from "@/shared/integrations/firebase/client";
import { ENDPOINT_BY_ID, type ParamValues } from "./developer-api";
import { Sidebar } from "./clientDeveloper/Sidebar";
import { EmptyPlayground, PlaygroundPanel } from "./clientDeveloper/PlaygroundPanel";
import { copyToClipboard, type RequestResult } from "./clientDeveloper/helpers";

export default function ClientDeveloper() {
  const { clientId } = useClientId();
  const [token, setToken] = useState<string | null>(null);
  const [tokenLoading, setTokenLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const initialId = (() => {
    const hash = window.location.hash.replace(/^#/, "");
    return ENDPOINT_BY_ID.has(hash) ? hash : "";
  })();
  const [selectedId, setSelectedId] = useState<string>(initialId);
  const [resultsByEndpoint, setResultsByEndpoint] = useState<
    Map<string, RequestResult>
  >(new Map());
  const [valuesByEndpoint, setValuesByEndpoint] = useState<
    Map<string, ParamValues>
  >(new Map());

  const handleResult = useCallback(
    (id: string, result: RequestResult | null) => {
      setResultsByEndpoint((prev) => {
        const next = new Map(prev);
        if (result == null) next.delete(id);
        else next.set(id, result);
        return next;
      });
    },
    [],
  );

  const handleValues = useCallback((id: string, values: ParamValues) => {
    setValuesByEndpoint((prev) => new Map(prev).set(id, values));
  }, []);

  const loadToken = useCallback(async (force: boolean, silent = false) => {
    if (force) setRefreshing(true);
    else setTokenLoading(true);
    try {
      const next = force
        ? await getFirebaseIdTokenWithRefresh()
        : await getFirebaseIdToken();
      setToken(next);
      if (force && !silent) toast.success("Token refreshed");
    } finally {
      setTokenLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    void loadToken(true, true);
    const interval = setInterval(() => void loadToken(true, true), 50 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadToken]);

  const onSelect = useCallback((id: string) => {
    setSelectedId(id);
    window.history.replaceState(null, "", `#${id}`);
  }, []);

  const selected = selectedId ? ENDPOINT_BY_ID.get(selectedId) : undefined;

  return (
    <div className="flex flex-col min-h-full">
      <header className="border-b border-border px-6 py-3 flex items-center justify-between gap-4 flex-wrap bg-background sticky top-0 z-10">
        <div className="flex items-center gap-3 flex-wrap min-w-0">
          <h1 className="text-base font-semibold tracking-tight">
            API Playground
          </h1>
        </div>

        <div className="flex items-center gap-2">
          <div className="hidden md:flex items-center gap-1.5 text-xs text-muted-foreground border border-border rounded px-2 py-1">
            <KeyRound className="h-3.5 w-3.5" />
            {tokenLoading ? (
              <Skeleton className="h-3 w-32" />
            ) : token ? (
              <>
                <span className="font-mono truncate max-w-[200px]">
                  {token.slice(0, 24)}…
                </span>
                <button
                  type="button"
                  onClick={() => copyToClipboard(token, "Token")}
                  className="hover:text-foreground transition-colors"
                  aria-label="Copy token"
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
              </>
            ) : (
              <span className="italic">no token</span>
            )}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void loadToken(true)}
            disabled={refreshing}
          >
            <RefreshCw
              className={cn("h-3.5 w-3.5 mr-2", refreshing && "animate-spin")}
            />
            Refresh token
          </Button>
        </div>
      </header>

      <div className="flex flex-1 items-stretch">
        <aside className="w-60 shrink-0 border-r border-border p-4 self-stretch">
          <div className="sticky top-[68px]">
            <Sidebar selectedId={selectedId} onSelect={onSelect} />
          </div>
        </aside>

        <main className="flex-1 min-w-0 p-6 lg:p-8">
          <div className="max-w-4xl">
            {selected ? (
              <PlaygroundPanel
                endpoint={selected}
                token={token}
                clientId={clientId ?? undefined}
                result={resultsByEndpoint.get(selected.id) ?? null}
                onResult={handleResult}
                values={valuesByEndpoint.get(selected.id) ?? null}
                onValues={handleValues}
              />
            ) : (
              <EmptyPlayground />
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
